import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { Notification } from './notification.model';
import { UtilService } from '../util/util.service';
import { INotificationService } from './inotification.service';
import { MessageService } from 'primeng/api';
import { WlUpperCaseFirstLetterPipe } from './../pipes/ucfirst.pipe';

@Injectable()
export class NotificationService implements INotificationService {
  public _notifications = new Subject<Notification>();
  public _utilService: UtilService;
  public _translateService: TranslateService;
  public constructor(
    translateService: TranslateService,
    utilService: UtilService,
    private _messageService: MessageService,
    private _ucFirstPipe: WlUpperCaseFirstLetterPipe
  ) {
    this._utilService = utilService;
    this._translateService = translateService;
  }

  public notificationAdded = this._notifications.asObservable();

  public add(type: string, title: string, message: string): void {
    let notification: Notification = new Notification();
    if (type != 'warn' && type != 'info' && type != 'success' && type != 'error') {
      notification.type = 'info';
    } else {
      notification.type = type;
    }

    if (this._utilService.isNullEmptyOrWhitespace(title)) {
      notification.title = type;
    } else {
      notification.title = title;
    }
    notification.message = message;

    this.messageServiceAdd(notification);
  }

  public info(...messages: string[]): void {
    messages.forEach((message) => {
      let notification: Notification = new Notification();
      let type: string = 'info';
      notification.type = type;
      notification.title = this._ucFirstPipe.transform(this._translateService.instant('common.info'));
      notification.message = message;

      this.messageServiceAdd(notification);
    });
  }

  public warn(...messages: string[]): void {
    messages.forEach((message) => {
      let notification: Notification = new Notification();
      let type: string = 'warn';
      notification.type = type;
      notification.title = this._ucFirstPipe.transform(this._translateService.instant('common.warning'));
      notification.message = message;
      notification.sticky = true;

      this.messageServiceAdd(notification);
    });
  }

  public success(...messages: string[]): void {
    messages.forEach((message) => {
      let notification: Notification = new Notification();
      let type: string = 'success';
      notification.type = type;
      notification.title = this._ucFirstPipe.transform(this._translateService.instant('common.success'));
      notification.message = message;

      this.messageServiceAdd(notification);
    });
  }

  public persistentSuccess(...messages: string[]): void {
    messages.forEach((message) => {
      let notification: Notification = new Notification();
      let type: string = 'success';
      notification.type = type;
      notification.title = this._ucFirstPipe.transform(this._translateService.instant('common.success'));
      notification.message = message;
      notification.sticky = true;

      this.messageServiceAdd(notification);
    });
  }

  public error(...messages: string[]): void {
    messages.forEach((message) => {
      let notification: Notification = new Notification();
      let type: string = 'error';
      notification.type = type;
      notification.title = this._ucFirstPipe.transform(this._translateService.instant('common.error'));
      notification.message = message;
      notification.sticky = true;
      this.messageServiceAdd(notification);
    });
  }

  public translateSuccess(translateKey: string) {
    this.success(this._translateService.instant(translateKey));
  }

  public translatePersistentSuccess(translateKey: string) {
    this.persistentSuccess(this._translateService.instant(translateKey));
  }

  public translateError(translateKey: string) {
    this.error(this._translateService.instant(translateKey));
  }

  public translateWarning(translateKey: string) {
    this.warn(this._translateService.instant(translateKey));
  }

  public translateInfo(translateKey: string) {
    this.info(this._translateService.instant(translateKey));
  }

  public httpError(error: any) {
    const message = this._utilService.extractErrorMessage(error);
    this.error(message);
  }

  private messageServiceAdd(notification: Notification) {
    this._messageService.add({
      severity: notification.type,
      summary: notification.title,
      detail: notification.message,
      sticky: notification.sticky,
    });
  }
}
